import CheckIcon from '@svgIcons/check.svg';
import CloseIcon from '@svgIcons/close.svg';

import styled, { css } from 'vue-styled-components';
import { mapGetters, mapState } from 'vuex';
import { applyHoverAndPress, COLORS } from '@styles/common.styled';

import {
    FLEX_COLUMN_DIV,
    FLEX_CENTER_DIV,
    FLEX_VERTICAL_CENTER_DIV,
} from '@styles/common.styled.component';

import { innerHtmlJSX, isValueExist } from '@util';

const LINKS = {
    ko: {
        googleAnalytics_no_permission:
            'https://academy.adriel.com/ko/%EB%A7%9E%EC%B6%A4-%EB%8C%80%ED%96%89/%ED%8C%8C%ED%8A%B8%EB%84%88/%EC%85%80%ED%94%84-%EC%9A%B4%EC%98%81-%EB%8D%B0%EC%9D%B4%ED%84%B0%EB%A5%BC-%EC%97%B0%EA%B2%B0%ED%95%98%EA%B3%A0-%EC%8B%B6%EC%96%B4%EC%9A%94',
        facebookPixel_inactive_pixel:
            'https://academy.adriel.com/ko/%EB%A7%9E%EC%B6%A4-%EB%8C%80%ED%96%89/%ED%8C%8C%ED%8A%B8%EB%84%88/%EC%85%80%ED%94%84-%EC%9A%B4%EC%98%81-%EB%8D%B0%EC%9D%B4%ED%84%B0%EB%A5%BC-%EC%97%B0%EA%B2%B0%ED%95%98%EA%B3%A0-%EC%8B%B6%EC%96%B4%EC%9A%94',
    },
    en: {
        googleAnalytics_no_permission:
            'https://academy.adriel.com/en/requirements-to-connect-an-ad-account',
        facebookPixel_inactive_pixel:
            'https://academy.adriel.com/en/requirements-to-connect-an-ad-account',
    },
    ja: {
        googleAnalytics_no_permission:
            'https://academy.adriel.com/en/requirements-to-connect-an-ad-account',
        facebookPixel_inactive_pixel:
            'https://academy.adriel.com/en/requirements-to-connect-an-ad-account',
    },
};

export default {
    props: {
        value: {
            type: Object,
            default: R.always({}),
        },
        active: {
            type: Boolean,
            default: false,
        },
        nameExtractor: {
            type: Function,
            default: R.always(''),
        },
        descriptionExtractor: {
            type: Function,
            default: R.always(''),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledInfo: {
            type: Object,
            default: R.always({}),
        },
        deletable: {
            type: Boolean,
            default: false,
        },
        selectable: {
            type: Boolean,
            default: true,
        },
        selected: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    computed: {
        ...mapState('user', ['subscription']),
        ...mapGetters('user', ['isAppsumo']),
        ...mapGetters('asset', ['connections']),
        tooltipText() {
            const { language, disabledInfo } = this;
            const { reasonText, type, channel } = disabledInfo;

            const href = LINKS[language][channel + '_' + type];

            return href ? (
                <a href={href} target="_blank">
                    {reasonText}
                </a>
            ) : (
                reasonText
            );
        },
    },
    methods: {
        renderRightSide() {
            let dom = [];

            if (this.disabled) {
                const { type = 'inactive' } = this.disabledInfo;

                dom.push(
                    <NameText
                        {...innerHtmlJSX(
                            this.$t(`CONNECTION.connection_box_status_${type}`)
                        )}
                    />
                );
            } else if (this.active) {
                dom.push(
                    <CheckIcon
                        v-view-box
                        width="16px"
                        height="16px"
                        fill={COLORS['blue-60']}
                    />
                );
            }

            if (this.deletable) {
                dom.push(
                    <CloseIcon
                        v-view-box
                        width="16px"
                        height="16px"
                        fill={COLORS['gray-30']}
                        class="close-icon"
                        onClick={() => this.$emit('delete')}
                    />
                );
            }

            return dom;
        },
        checkAppsumoLimited() {
            const { selected, connections, subscription } = this;
            const { plan_detail = {} } = subscription;

            return (
                plan_detail.assets - connections.length - selected.length < 1
            );
        },
    },
    render() {
        const {
            active,
            disabled,
            disabledInfo,
            value,
            selectable,
            wrapTooltip,
            tooltipText,
            isAppsumo,
            checkAppsumoLimited,
            showAppsumoLimitReachedToast,
        } = this;

        return wrapTooltip({
            placement: 'bottom',
            visible: isValueExist(disabledInfo),
            title: tooltipText,
            delay: 0.5,
            content: (
                <ElemBox
                    selected={active}
                    disabled={disabled}
                    selectable={selectable}
                    onClick={() => {
                        if (disabled) return;
                        if (isAppsumo && !active && checkAppsumoLimited()) {
                            showAppsumoLimitReachedToast();
                            return;
                        }
                        this.$emit('click');
                    }}
                    class="connection-box"
                >
                    <ElemLeft selected={active}>
                        <NameText selected={active}>
                            {this.nameExtractor(value)}
                        </NameText>
                        <PropText>{this.descriptionExtractor(value)}</PropText>
                    </ElemLeft>
                    <ElemRight>{this.renderRightSide()}</ElemRight>
                    {this.$slots.default && (
                        <SlotWrapper>{this.$slots.default}</SlotWrapper>
                    )}
                </ElemBox>
            ),
        });
    },
};

const ElemBox = styled(FLEX_VERTICAL_CENTER_DIV, {
    selected: Boolean,
    disabled: Boolean,
    selectable: Boolean,
})`
    padding: 12px 16px;
    min-height: 50px;
    flex-shrink: 0;
    border-radius: 4px;
    border: solid 1px ${COLORS['gray-25']};
    margin: 6px 0;
    cursor: pointer;
    ${applyHoverAndPress('white', 'background-color')};
    justify-content: space-between;
    flex-wrap: wrap;

    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${COLORS['gray-10']};
            cursor: not-allowed;
            &:hover,
            &:active {
                background-color: ${COLORS['gray-10']};
            }

            div span {
                color: ${COLORS['gray-30']};
            }
        `};
    pointer-events: ${({ selectable }) => (selectable ? 'auto' : 'none')};
`;

const ElemLeft = styled(FLEX_COLUMN_DIV, { selected: Boolean })`
    align-items: flex-start;
    max-width: ${({ selected }) => (selected ? 'calc(100% - 40px)' : '100%')};
`;

const NameText = styled('span', { selected: Boolean })`
    font-size: 14px;
    color: ${({ selected }) =>
        selected ? COLORS['blue-60'] : COLORS['gray-100']};

    a {
        color: ${({ selected }) =>
            selected ? COLORS['blue-60'] : COLORS['gray-100']};
    }

    @media screen and (max-width: 360px) {
        font-size: 13px;
    }
`;

const PropText = styled('span', { selected: Boolean })`
    font-size: 13px;
    color: ${COLORS['gray-50']};
    margin-top: 4px;
    max-width: 400px;
    word-break: break-all;

    @media screen and (max-width: 359px) {
        font-size: 12px;
    }
`;

const ElemRight = styled(FLEX_CENTER_DIV)`
    & > svg {
        width: 20px;
        height: 20px;
        &.close-icon {
            cursor: pointer;
        }
    }
`;

const SlotWrapper = styled.div`
    width: 100%;
`;
